import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useSWR from 'swr';

import {
  applyMapSourceGeometryOverride,
  getMapSourceGeometryKinds,
  normalizeMapSourceGeometryKind,
  readMapSourceVectorLayers,
  type MapSourceAuthoringGeometryKind,
  type MapSourceGeometryKind,
  type MapSourceVectorLayerRecord,
} from '@workspace/ui';
import {
  VerticalStack,
  HorizontalStack,
  SectionEyebrow,
  DescriptionText,
  TechnicalValue,
  MetricBar,
  CondensedToolbar,
  ToolbarItem,
  RegistryList,
  RegistryRow,
  RegistryStatus,
  SourceIcon,
  WorksurfaceHeader,
  SilkInput,
  SilkSelect,
  SectionSeparator,
  PropertyGrid,
  PaneHeader,
  PaneSection,
  PaneFooter,
  Callout,
  Pagination,
  AdminShell,
  WorksurfaceTitle,
  WorksurfaceLayout,
  SidebarPane,
  MainPane,
  Scrollable,
  Overlay,
  Surface,
  MapPreviewFrame,
  MapSourceLayerVisibilityPanel,
  MapFeatureHoverCard,
  ManagementRegistry,
  RoutingGraphImportStatusView,
} from '@workspace/silk-ui';
import {
  Button,
  MapWidgetView,
  type MapWorkbenchFeatureInspection,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  Textarea,
} from '@workspace/ui';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Plus, Search, Filter, MoreHorizontal, Settings2, Shield, Info, Map as MapIcon, Database, Globe } from 'lucide-react';

import { MappingAssetAccessSheet } from '@/components/mapping/MappingAssetAccessSheet';
import { MappingFeatureUnavailable, MappingPermissionDenied } from '@/components/mapping/MappingFeatureUnavailable';
import {
  clearPersistedMapSourceUploadSnapshot,
  estimateMapSourceUploadProgress,
  fingerprintMapSourceUploadFile,
  isMapSourceUploadBackgroundProcessing,
  isMapSourceUploadAwaitingMultipartFile,
  isMapSourceUploadTerminalStatus,
  loadPersistedMapSourceUploadSnapshot,
  persistMapSourceUploadSnapshot,
  uploadMapSourcePart,
} from '@/lib/mapSourceUploadSession';
import {
  formatShareScopeLabel,
  isPortalAssetReadOnly,
  normalizeShareScope,
} from '@/lib/mapping-assets';
import {
  summarizeSalesMapSourcePackage,
} from '@/lib/salesMapSourcePackage';
import { getGeoJSONSourceDiagnostics } from '@/lib/geojsonSourceDiagnostics';
import { useMappingAccess } from '@/lib/useMappingAccess';
import { useRoutingGraphImportStatusController } from '@/features/routing-graph/useRoutingGraphImportStatusController';
import {
  abortMapSourceUpload,
  completeMapSourceUpload,
  createLiveGeoJSONMapSource,
  deleteMapSource,
  getMapSource,
  getMapSourceUpload,
  initMapSourceUpload,
  listMapSourceUploads,
  listMapSourceShares,
  listMapSources,
  presignMapSourceUploadPart,
  previewMapStyle,
  replaceMapSourceShares,
  updateMapSource,
  type MapSource,
  type MapSourceUploadSession,
  type MapSourceUpdateRequest,
  type MapStylePreviewResponse,
  type MapViewState,
  type ShareScope,
} from '@/lib/mapping-api';

const DEFAULT_PAGE_SIZE = 200;
const DEFAULT_VIEW_STATE: MapViewState = {
  center: [-84.5, 44.5],
  zoom: 7,
  bearing: 0,
  pitch: 0,
};

type VectorLayerMeta = MapSourceVectorLayerRecord;

type InspectorLayer = VectorLayerMeta & {
  color: string;
  styleLayerIds: string[];
};

type InspectorFeature = {
  id?: string | number;
  source?: string;
  sourceLayer?: string;
  layerId?: string;
  geometryType?: string;
  properties?: Record<string, unknown>;
  rawJson: Record<string, unknown>;
};

const formatOptionalJSON = (value?: Record<string, unknown> | null) =>
  value && Object.keys(value).length > 0 ? JSON.stringify(value, null, 2) : '';

const normalizeVectorLayers = (layers?: Array<Record<string, unknown> | string> | null): VectorLayerMeta[] =>
  readMapSourceVectorLayers(layers);

const buildLayerColor = (index: number) => {
  const hue = (index * 57) % 360;
  return `hsl(${hue} 68% 48%)`;
};

const sanitizeLayerId = (value: string, fallback: string) => {
  const cleaned = value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  return cleaned || fallback;
};

const buildInspectorLayers = (layers?: Array<Record<string, unknown> | string> | null): InspectorLayer[] => {
  const normalized = normalizeVectorLayers(layers);
  return normalized.map((layer, index) => {
    const safeId = sanitizeLayerId(layer.id, `layer-${index + 1}`);
    const baseId = `tileset-${safeId}-${index + 1}`;
    return {
      ...layer,
      color: buildLayerColor(index),
      styleLayerIds: [`${baseId}-fill`, `${baseId}-outline`, `${baseId}-line-casing`, `${baseId}-line`, `${baseId}-circle`],
    };
  });
};

const getVectorLayerGeometryFlags = (geometry?: MapSourceGeometryKind | string) => {
  const kinds = getMapSourceGeometryKinds(typeof geometry === 'string' ? geometry : geometry ?? 'unknown');
  return {
    isLine: kinds.includes('line'),
    isPoint: kinds.includes('point'),
    isPolygon: kinds.includes('polygon'),
  };
};

const buildInspectorStyle = (source: MapSource, layers: InspectorLayer[]) => {
  const styleLayers: Array<Record<string, unknown>> = [
    { id: 'background', type: 'background', paint: { 'background-color': 'rgba(15, 23, 42, 0.04)' } },
  ];

  layers.forEach(layer => {
    const { isLine, isPoint, isPolygon } = getVectorLayerGeometryFlags(layer.effective_geometry);
    const common: Record<string, unknown> = { source: 'dataset', minzoom: layer.minzoom };
    if (!isGeoJSONSource(source)) common['source-layer'] = layer.id;
    
    if (isPolygon) {
      styleLayers.push(
        { id: `${layer.styleLayerIds[0]}`, type: 'fill', ...common, paint: { 'fill-color': layer.color, 'fill-opacity': 0.18 } },
        { id: `${layer.styleLayerIds[1]}`, type: 'line', ...common, layout: { 'line-cap': 'butt', 'line-join': 'miter' }, paint: { 'line-color': layer.color, 'line-width': 1.5, 'line-opacity': 0.9 } },
      );
    }
    if (isLine) {
      styleLayers.push(
        { id: `${layer.styleLayerIds[2]}`, type: 'line', ...common, paint: { 'line-color': 'rgba(15, 23, 42, 0.18)', 'line-width': 4, 'line-opacity': 0.95 } },
        { id: `${layer.styleLayerIds[3]}`, type: 'line', ...common, paint: { 'line-color': layer.color, 'line-width': 2.25, 'line-opacity': 0.98 } },
      );
    }
    if (isPoint) {
      styleLayers.push({ id: `${layer.styleLayerIds[4]}`, type: 'circle', ...common, paint: { 'circle-color': layer.color, 'circle-radius': 4.5, 'circle-stroke-color': 'rgba(15, 23, 42, 0.4)', 'circle-stroke-width': 1.25 } });
    }
  });

  return {
    version: 8,
    name: 'Inspector',
    sources: {
      dataset: {
        ...(isGeoJSONSource(source)
          ? { type: 'geojson', data: buildPortalSourceRuntimeUrl(source) }
          : { type: 'vector', url: `pmtiles://${source.id}` }),
      },
    },
    layers: styleLayers,
  };
};

const resolveMapRequestUrl = (url: string) => {
  if (!url) return url;
  const trimmed = url.trim();
  if (/^[a-z][a-z0-9+.-]*:/i.test(trimmed)) return trimmed;
  const origin = typeof window !== 'undefined' ? window.location.origin : null;
  if (!origin) return trimmed;
  try {
    return new URL(trimmed, origin).toString().replace(/%7B/gi, '{').replace(/%7D/gi, '}');
  } catch {
    return trimmed;
  }
};

const normalizeStyleUrls = (style: Record<string, unknown>) => {
  const clone = JSON.parse(JSON.stringify(style));
  const remap = (value?: string) => (typeof value === 'string' ? resolveMapRequestUrl(value) : value);
  if (typeof clone.sprite === 'string') clone.sprite = remap(clone.sprite);
  if (typeof clone.glyphs === 'string') clone.glyphs = remap(clone.glyphs);
  if (clone.sources && typeof clone.sources === 'object') {
    Object.values(clone.sources).forEach((source: any) => {
      if (Array.isArray(source.tiles)) source.tiles = source.tiles.map((tile: string) => remap(tile));
      if (typeof source.url === 'string') source.url = remap(source.url);
      if (typeof source.data === 'string') source.data = remap(source.data);
    });
  }
  return clone;
};

const buildGeoJSONRetryPayload = (source: MapSource): MapSourceUpdateRequest | null => {
  if (!isGeoJSONSource(source)) return null;
  if (source.request_recipe) return { request_recipe: JSON.parse(JSON.stringify(source.request_recipe)) };
  if (source.geojson_url?.trim()) return { url: source.geojson_url.trim() };
  return null;
};

const formatDate = (value?: string | null) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
};

const formatBytes = (value?: number | null) => {
  if (value == null || !Number.isFinite(value) || value <= 0) return null;
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let size = value;
  let unitIndex = 0;
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex += 1;
  }
  const rounded = size >= 100 || unitIndex === 0 ? size.toFixed(0) : size.toFixed(1);
  return `${rounded} ${units[unitIndex]}`;
};

const formatUploadDuration = (seconds?: number | null) => {
  if (seconds == null || !Number.isFinite(seconds) || seconds < 0) return null;
  const rounded = Math.max(0, Math.round(seconds));
  const hours = Math.floor(rounded / 3600);
  const minutes = Math.floor((rounded % 3600) / 60);
  const secs = rounded % 60;
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${secs}s`;
  return `${secs}s`;
};

type GeoJSONFormAuthStrategy = 'none' | 'bearer_token' | 'header_api_key';
type MapSourceFormKind = 'geojson_file' | 'geojson_http';
type UploadPhase =
  | 'idle'
  | 'preparing'
  | 'uploading'
  | 'finalizing'
  | 'queued_processing'
  | 'processing'
  | 'inspecting'
  | 'registering'
  | 'ready'
  | 'failed'
  | 'cancelled'
  | 'expired';

const uploadPhaseLabelForSession = (phase: UploadPhase) => {
  switch (phase) {
    case 'preparing':
      return 'Preparing upload session';
    case 'uploading':
      return 'Uploading parts';
    case 'finalizing':
      return 'Finalizing multipart upload';
    case 'queued_processing':
      return 'Queued for runtime build';
    case 'processing':
      return 'Building runtime artifacts';
    case 'inspecting':
      return 'Inspecting generated runtime tiles';
    case 'registering':
      return 'Registering source';
    case 'ready':
      return 'Upload complete';
    case 'cancelled':
      return 'Upload cancelled';
    case 'expired':
      return 'Upload expired';
    case 'failed':
      return 'Upload failed';
    default:
      return 'Ready to upload';
  }
};

const isGeoJSONSource = (source?: Pick<MapSource, 'source_kind'> | null) =>
  source?.source_kind === 'geojson_http' || source?.source_kind === 'geojson_managed';

const getGeoJSONAuthStatusLabel = (source?: Pick<MapSource, 'auth_strategy' | 'auth_metadata'> | null) => {
  if (!source?.auth_strategy || source.auth_strategy === 'none') {
    return 'No upstream auth configured.';
  }
  if (source.auth_strategy === 'bearer_token') {
    return 'Bearer token configured.';
  }
  const headerName = source.auth_metadata?.header_name?.trim();
  return headerName ? `Header ${headerName} configured.` : 'Header API key configured.';
};

const buildPortalSourceRuntimeUrl = (source?: Pick<MapSource, 'id' | 'source_kind'> | null) =>
  source?.id
    ? isGeoJSONSource(source)
      ? `/api/v1/mapping/map-sources/${encodeURIComponent(source.id)}/data.geojson`
      : `/api/v1/mapping/map-sources/${encodeURIComponent(source.id)}/archive`
    : '';

const formatDebugJSON = (value?: Record<string, unknown> | null) => (value ? JSON.stringify(value, null, 2) : '{}');
const formatSalesMapLayerLabel = (value: string) => value.replace(/_/g, ' ');

const GeoJSONValidationAlert = ({ source }: { source?: Pick<MapSource, 'source_kind' | 'metadata' | 'vector_layers' | 'auth_strategy' | 'auth_metadata' | 'request_recipe'> | null }) => {
  if (!isGeoJSONSource(source)) return null;
  const diagnostics = getGeoJSONSourceDiagnostics(source);
  if (diagnostics.state !== 'error') return null;
  return (
    <Callout variant="error">
      <VerticalStack gap={2}>
        <DescriptionText className="font-bold">{diagnostics.statusLabel}</DescriptionText>
        <DescriptionText>{diagnostics.detail}</DescriptionText>
        {diagnostics.guidance && <DescriptionText className="text-xs">Check: {diagnostics.guidance}</DescriptionText>}
      </VerticalStack>
    </Callout>
  );
};

const GeoJSONDebugDialog = ({ source, open, canRetry, retrying, retryError, onOpenChange, onRetry }: { source: MapSource | null, open: boolean, canRetry: boolean, retrying: boolean, retryError?: string | null, onOpenChange: (open: boolean) => void, onRetry: () => void }) => {
  const diagnostics = getGeoJSONSourceDiagnostics(source);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] max-w-4xl overflow-y-auto">
        <DialogHeader><DialogTitle>Live GeoJSON debug details</DialogTitle></DialogHeader>
        {source && (
          <VerticalStack gap={6}>
            <GeoJSONValidationAlert source={source} />
            {retryError && <Callout variant="error">{retryError}</Callout>}
            <WorksurfaceLayout className="h-auto">
              <SidebarPane className="border-none bg-muted/10 rounded-lg p-4" width="50%">
                <SectionEyebrow>Fetch Status</SectionEyebrow>
                <PropertyGrid items={[
                  { label: 'Status', value: diagnostics.statusLabel },
                  { label: 'HTTP', value: diagnostics.lastFetchStatus != null ? String(diagnostics.lastFetchStatus) : '—' },
                  { label: 'Checked', value: formatDate(diagnostics.lastFetchedAt) },
                  { label: 'Last Good', value: formatDate(diagnostics.lastSuccessfulFetchAt) },
                ]} />
              </SidebarPane>
              <MainPane className="bg-muted/10 rounded-lg p-4 ml-4">
                <SectionEyebrow>Request Summary</SectionEyebrow>
                <PropertyGrid items={[
                  { label: 'URL', value: source.geojson_url ?? '—', isTechnical: true },
                  { label: 'Auth', value: getGeoJSONAuthStatusLabel(source) },
                ]} />
              </MainPane>
            </WorksurfaceLayout>
            <VerticalStack gap={4}>
              <SectionEyebrow>Recipe JSON</SectionEyebrow>
              <Scrollable className="max-h-48 bg-muted/40 p-3 rounded font-mono text-[10px] whitespace-pre-wrap">
                {formatDebugJSON(source.request_recipe)}
              </Scrollable>
            </VerticalStack>
          </VerticalStack>
        )}
        <DialogFooter showCloseButton>
          <Button onClick={onRetry} disabled={!source?.id || retrying || !canRetry}>
            {retrying ? 'Retrying…' : 'Retry fetch'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const normalizeInspectorFeature = (feature: MapWorkbenchFeatureInspection | null | undefined): InspectorFeature | null => {
  if (!feature) return null;
  return {
    id: feature.id as string | number,
    source: feature.source,
    sourceLayer: feature.sourceLayer,
    layerId: feature.layerId,
    geometryType: feature.geometryType,
    properties: (feature.properties ?? {}) as Record<string, unknown>,
    rawJson: feature.rawJson,
  };
};

function MapInspectorView({
  styleJson,
  viewState,
  inspectableLayerIds,
  onHoverFeatureChange,
  onPinnedFeatureChange,
}: {
  styleJson: Record<string, unknown> | null;
  viewState: MapViewState | null;
  inspectableLayerIds: string[];
  onHoverFeatureChange: (feature: InspectorFeature | null) => void;
  onPinnedFeatureChange: (feature: InspectorFeature | null) => void;
}) {
  return (
    <MainPane className="rounded-lg border border-border/50 overflow-hidden bg-card">
      <Overlay position="inset" id="map-container-host">
        <Surface className="h-full w-full">
          <MapWidgetView
            className="h-full rounded-none border-0 bg-transparent"
            styleJson={styleJson}
            status={styleJson ? 'success' : 'loading'}
            defaultViewState={viewState ?? DEFAULT_VIEW_STATE}
            inspectableLayerIds={inspectableLayerIds}
            activeToolId="select"
            drawnGeometry={null}
            onHoverFeatureChange={feature => onHoverFeatureChange(normalizeInspectorFeature(feature))}
            onPinnedFeatureChange={feature => onPinnedFeatureChange(normalizeInspectorFeature(feature))}
          />
        </Surface>
      </Overlay>
    </MainPane>
  );
}

export default function MapSourcesPage() {
  const mappingAccess = useMappingAccess();
  const routingGraphStatus = useRoutingGraphImportStatusController();
  const [shareScopeFilter, setShareScopeFilter] = useState<'all' | ShareScope>('all');
  const [search, setSearch] = useState('');
  const [sheetOpen, setSheetOpen] = useState(false);
  const [replaceTarget, setReplaceTarget] = useState<MapSource | null>(null);
  const [accessOpen, setAccessOpen] = useState(false);
  const [accessTarget, setAccessTarget] = useState<MapSource | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [uploadSession, setUploadSession] = useState<MapSourceUploadSession | null>(null);
  const [uploadPhase, setUploadPhase] = useState<UploadPhase>('idle');
  const [uploadBytes, setUploadBytes] = useState(0);
  const [uploadRateBytes, setUploadRateBytes] = useState(0);
  const [uploadEtaSeconds, setUploadEtaSeconds] = useState<number | null>(null);
  const [resumeNeedsFile, setResumeNeedsFile] = useState(false);
  const [geojsonRetryingSourceId, setGeojsonRetryingSourceId] = useState<string | null>(null);
  const [geojsonRetryError, setGeojsonRetryError] = useState<string | null>(null);
  const [debugTarget, setDebugTarget] = useState<MapSource | null>(null);
  const [debugOpen, setDebugOpen] = useState(false);
  const [formState, setFormState] = useState({
    sourceKind: 'geojson_file' as MapSourceFormKind,
    name: '',
    description: '',
    shareScope: 'tenant' as ShareScope,
    layerId: '',
    url: '',
    requestRecipeText: '',
    serverFilteringText: '',
    authStrategy: 'none' as GeoJSONFormAuthStrategy,
    authHeaderName: '',
    authSecret: '',
    file: null as File | null,
  });
  const [inspectorOpen, setInspectorOpen] = useState(false);
  const [inspectorTarget, setInspectorTarget] = useState<MapSource | null>(null);
  const [inspectorLayers, setInspectorLayers] = useState<InspectorLayer[]>([]);
  const [layerVisibility, setLayerVisibility] = useState<Record<string, boolean>>({});
  const [inspectorStyle, setInspectorStyle] = useState<Record<string, unknown> | null>(null);
  const [inspectorViewState, setInspectorViewState] = useState<MapViewState | null>(null);
  const [inspectorStatus, setInspectorStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [inspectorError, setInspectorError] = useState<string | null>(null);
  const [hoveredFeature, setHoveredFeature] = useState<InspectorFeature | null>(null);
  const [pinnedFeature, setPinnedFeature] = useState<InspectorFeature | null>(null);
  const [page, setPage] = useState(1);
  const uploadAbortControllerRef = useRef<AbortController | null>(null);
  const uploadActiveBytesRef = useRef<Record<number, number>>({});
  const uploadCompletedBytesRef = useRef(0);
  const uploadProgressSampleRef = useRef<{ bytes: number; time: number } | null>(null);

  const { data, isLoading, mutate } = useSWR<MapSource[]>(
    mappingAccess.accessEnabled ? ['portal-map-sources', shareScopeFilter] : null,
    () => listMapSources({ limit: DEFAULT_PAGE_SIZE, share_scope: shareScopeFilter === 'all' ? undefined : shareScopeFilter }),
  );
  const { data: uploadSessions } = useSWR<MapSourceUploadSession[]>(
    mappingAccess.accessEnabled ? ['portal-map-source-uploads'] : null,
    () => listMapSourceUploads({ limit: 50 }),
    {
      refreshInterval: currentSessions => {
        const sessions = Array.isArray(currentSessions) ? currentSessions : [];
        return sessions.some(session => isMapSourceUploadBackgroundProcessing(session.status)) ? 2000 : 10000;
      },
    },
  );

  const filteredSources = useMemo(() => {
    if (!data) return [];
    const needle = search.trim().toLowerCase();
    return data.filter(s => [s.name, s.description, s.id].some(v => v?.toLowerCase().includes(needle)));
  }, [data, search]);

  const uploadPhaseLabel = useMemo(() => {
    if (uploadSession && isMapSourceUploadBackgroundProcessing(uploadSession.status)) {
      return estimateMapSourceUploadProgress(uploadSession).phaseLabel;
    }
    switch (uploadPhase) {
      case 'preparing':
        return 'Preparing upload session';
      case 'uploading':
        return 'Uploading parts';
      case 'finalizing':
        return 'Finalizing multipart upload';
      case 'queued_processing':
        return 'Queued for runtime build';
      case 'processing':
        return 'Building runtime artifacts';
      case 'inspecting':
        return 'Inspecting generated runtime tiles';
      case 'registering':
        return 'Registering source';
      case 'ready':
        return 'Upload complete';
      case 'cancelled':
        return 'Upload cancelled';
      case 'expired':
        return 'Upload expired';
      case 'failed':
        return 'Upload failed';
      default:
        return 'Ready to upload';
    }
  }, [uploadPhase, uploadSession]);

  const applyUploadProgressSample = useCallback((totalBytes: number) => {
    const activeBytes = Object.values(uploadActiveBytesRef.current).reduce((sum, value) => sum + value, 0);
    const uploaded = Math.min(totalBytes, uploadCompletedBytesRef.current + activeBytes);
    const now = performance.now();
    const previous = uploadProgressSampleRef.current;

    setUploadBytes(uploaded);
    if (previous) {
      const deltaBytes = uploaded - previous.bytes;
      const deltaSeconds = (now - previous.time) / 1000;
      if (deltaBytes > 0 && deltaSeconds > 0.25) {
        const nextRate = deltaBytes / deltaSeconds;
        setUploadRateBytes(nextRate);
        setUploadEtaSeconds(nextRate > 0 ? Math.max(0, (totalBytes - uploaded) / nextRate) : null);
        uploadProgressSampleRef.current = { bytes: uploaded, time: now };
        return;
      }
    }
    uploadProgressSampleRef.current = { bytes: uploaded, time: now };
  }, []);

  const syncUploadProgressFromSession = useCallback((session: MapSourceUploadSession | null) => {
    const uploaded = session?.uploaded_bytes ?? 0;
    uploadCompletedBytesRef.current = uploaded;
    uploadActiveBytesRef.current = {};
    uploadProgressSampleRef.current = { bytes: uploaded, time: performance.now() };
    setUploadBytes(uploaded);
    setUploadRateBytes(0);
    setUploadEtaSeconds(null);
  }, []);

  const mapUploadStatusToPhase = useCallback((session: MapSourceUploadSession | null): UploadPhase => {
    switch (session?.status) {
      case 'pending_upload':
        return 'preparing';
      case 'uploading':
        return 'uploading';
      case 'finalizing':
        return 'finalizing';
      case 'queued_processing':
        return 'queued_processing';
      case 'processing':
        return 'processing';
      case 'inspecting':
        return 'inspecting';
      case 'registering':
        return 'registering';
      case 'ready':
        return 'ready';
      case 'cancelled':
        return 'cancelled';
      case 'expired':
        return 'expired';
      case 'failed':
        return 'failed';
      default:
        return 'idle';
    }
  }, []);

  const visiblePendingUploads = useMemo(
    () =>
      (uploadSessions ?? []).filter(
        session =>
          isMapSourceUploadBackgroundProcessing(session.status) ||
          session.status === 'failed' ||
          session.status === 'expired' ||
          session.status === 'cancelled',
      ),
    [uploadSessions],
  );

  const clearUploadState = useCallback(() => {
    uploadAbortControllerRef.current = null;
    uploadActiveBytesRef.current = {};
    uploadCompletedBytesRef.current = 0;
    uploadProgressSampleRef.current = null;
    setUploadSession(null);
    setUploadPhase('idle');
    setUploadBytes(0);
    setUploadRateBytes(0);
    setUploadEtaSeconds(null);
    setResumeNeedsFile(false);
  }, []);

  const resetForm = useCallback((target?: MapSource | null) => {
    setFormError(null);
    setSaving(false);
    clearUploadState();
    setFormState({
      sourceKind: target?.source_kind === 'geojson_http' ? 'geojson_http' : 'geojson_file',
      name: target?.name ?? '',
      description: target?.description ?? '',
      shareScope: normalizeShareScope(target?.share_scope),
      layerId: '',
      url:
        typeof target?.request_recipe?.url === 'string'
          ? String(target.request_recipe.url)
          : target?.geojson_url ?? '',
      requestRecipeText: formatOptionalJSON(target?.request_recipe),
      serverFilteringText: formatOptionalJSON(target?.server_filtering),
      authStrategy: target?.auth_strategy ?? 'none',
      authHeaderName: target?.auth_metadata?.header_name ?? '',
      authSecret: '',
      file: null,
    });
  }, [clearUploadState]);

  const refreshInspectorFromSource = useCallback(async (source: MapSource) => {
    setInspectorTarget(source);
    setInspectorStatus('loading');
    const layers = buildInspectorLayers(source.vector_layers ?? null);
    setInspectorLayers(layers);
    setLayerVisibility(Object.fromEntries(layers.map(l => [l.id, true])));
    try {
      const payload = await previewMapStyle({ style_json: buildInspectorStyle(source, layers) }) as MapStylePreviewResponse;
      setInspectorStyle(normalizeStyleUrls(payload.style_json));
      setInspectorViewState(payload.view_state);
      setInspectorStatus('success');
    } catch (err: any) {
      setInspectorError(err.message);
      setInspectorStatus('error');
    }
  }, []);

  const openInspector = useCallback(async (source: MapSource) => {
    setInspectorOpen(true);
    await refreshInspectorFromSource(source);
  }, [refreshInspectorFromSource]);

  const handleCloseInspector = () => {
    setInspectorOpen(false);
    setInspectorTarget(null);
    setInspectorStatus('idle');
  };

  const openUpload = (target?: MapSource) => {
    setReplaceTarget(target ?? null);
    setSheetOpen(true);
    resetForm(target ?? null);
  };

  const runMultipartUpload = useCallback(async (session: MapSourceUploadSession, file: File) => {
    if (!session.id) {
      throw new Error('Upload session id is missing.');
    }

    const totalBytes = session.size_bytes ?? file.size;
    const partSize = session.part_size_bytes ?? 64 * 1024 * 1024;
    const totalParts =
      session.total_parts ?? Math.max(1, Math.ceil((session.size_bytes ?? file.size) / Math.max(1, partSize)));
    const uploadedParts = new Set(
      (session.uploaded_parts ?? [])
        .map(part => part.part_number)
        .filter((value): value is number => typeof value === 'number' && value > 0),
    );

    uploadCompletedBytesRef.current =
      session.uploaded_bytes ??
      (session.uploaded_parts ?? []).reduce((sum, part) => sum + (part.size_bytes ?? 0), 0);
    uploadActiveBytesRef.current = {};
    uploadProgressSampleRef.current = { bytes: uploadCompletedBytesRef.current, time: performance.now() };
    setUploadBytes(uploadCompletedBytesRef.current);
    setUploadRateBytes(0);
    setUploadEtaSeconds(null);
    setResumeNeedsFile(false);
    setUploadPhase('uploading');

    const pendingParts = Array.from({ length: totalParts }, (_, index) => index + 1).filter(
      partNumber => !uploadedParts.has(partNumber),
    );
    if (pendingParts.length === 0) {
      setUploadPhase('finalizing');
      return;
    }

    const controller = new AbortController();
    uploadAbortControllerRef.current = controller;
    let nextPartIndex = 0;
    const workerCount = Math.min(4, pendingParts.length);

    const uploadNextPart = async () => {
      while (nextPartIndex < pendingParts.length) {
        if (controller.signal.aborted) {
          throw new DOMException('Upload cancelled', 'AbortError');
        }
        const currentIndex = nextPartIndex;
        nextPartIndex += 1;
        const partNumber = pendingParts[currentIndex];
        const partStart = (partNumber - 1) * partSize;
        const partEnd = Math.min(file.size, partStart + partSize);
        const partBlob = file.slice(partStart, partEnd);

        const presignedPart = await presignMapSourceUploadPart(session.id, partNumber);
        if (!presignedPart.url) {
          throw new Error('Failed to presign upload part.');
        }

        await uploadMapSourcePart(
          presignedPart.url,
          partBlob,
          loadedBytes => {
            uploadActiveBytesRef.current[partNumber] = loadedBytes;
            applyUploadProgressSample(totalBytes);
          },
          controller.signal,
        );

        delete uploadActiveBytesRef.current[partNumber];
        uploadCompletedBytesRef.current += partBlob.size;
        applyUploadProgressSample(totalBytes);
      }
    };

    await Promise.all(Array.from({ length: workerCount }, () => uploadNextPart()));
    uploadAbortControllerRef.current = null;
    setUploadPhase('finalizing');
  }, [applyUploadProgressSample]);

  const closeUploadSheet = useCallback(() => {
    setSheetOpen(false);
    setReplaceTarget(null);
    clearPersistedMapSourceUploadSnapshot();
    resetForm(null);
  }, [resetForm]);

  const handleSubmit = async () => {
    setFormError(null);

    const resolvedName = formState.name.trim() || replaceTarget?.name || '';
    if (!resolvedName) {
      setFormError('Name is required.');
      return;
    }

    if (formState.sourceKind === 'geojson_http') {
      let requestRecipe: Record<string, unknown> | undefined;
      let serverFiltering: Record<string, unknown> | undefined;
      try {
        requestRecipe = formState.requestRecipeText.trim()
          ? (JSON.parse(formState.requestRecipeText) as Record<string, unknown>)
          : undefined;
        serverFiltering = formState.serverFilteringText.trim()
          ? (JSON.parse(formState.serverFilteringText) as Record<string, unknown>)
          : undefined;
      } catch {
        setFormError('Request recipe and server filtering must be valid JSON objects.');
        return;
      }

      setSaving(true);
      try {
        const payload = {
          name: resolvedName,
          description: formState.description.trim() || undefined,
          share_scope: formState.shareScope,
          url: formState.url.trim() || undefined,
          request_recipe: requestRecipe,
          server_filtering: serverFiltering,
          auth_strategy: formState.authStrategy,
          auth_metadata:
            formState.authStrategy === 'header_api_key'
              ? { header_name: formState.authHeaderName.trim() }
              : undefined,
          auth_secret: formState.authSecret.trim() || undefined,
        };
        replaceTarget?.id
          ? await updateMapSource(replaceTarget.id, payload as any)
          : await createLiveGeoJSONMapSource(payload as any);
        await mutate();
        closeUploadSheet();
      } catch (err: any) {
        setFormError(err.message);
      } finally {
        setSaving(false);
      }
      return;
    }

    if (!isBinaryUploadForm) {
      return;
    }

    if (!formState.file) {
      setFormError('Select a GeoJSON file before uploading.');
      return;
    }

    const fileFingerprint = fingerprintMapSourceUploadFile(formState.file);
    const persistedSnapshot = loadPersistedMapSourceUploadSnapshot();

    setSaving(true);
    try {
      let activeSession = uploadSession;
      if (activeSession?.id) {
        if (persistedSnapshot) {
          const persistedFingerprint = `${persistedSnapshot.fileName}:${persistedSnapshot.fileSize}:${persistedSnapshot.fileLastModified}`;
          if (persistedFingerprint !== fileFingerprint) {
            throw new Error('Re-select the same GeoJSON file to resume this upload session.');
          }
        }
      } else {
        setUploadPhase('preparing');
        activeSession = await initMapSourceUpload({
          filename: formState.file.name,
          mime_type: formState.file.type || 'application/geo+json',
          size_bytes: formState.file.size,
          name: resolvedName,
          description: formState.description.trim() || undefined,
          share_scope: formState.shareScope,
          processing_profile: 'geojson_to_pmtiles',
          layer_id: formState.layerId.trim() || undefined,
          source_id: replaceTarget?.id,
        });
        setUploadSession(activeSession);
      }

      if (!activeSession?.id) {
        throw new Error('Upload session was not created.');
      }

      persistMapSourceUploadSnapshot({
        sessionId: activeSession.id,
        fileName: formState.file.name,
        fileSize: formState.file.size,
        fileLastModified: formState.file.lastModified,
        replaceTargetId: replaceTarget?.id ?? null,
        name: resolvedName,
        description: formState.description.trim() || undefined,
        shareScope: formState.shareScope,
        sourceKind: formState.sourceKind,
        processingProfile: 'geojson_to_pmtiles',
        layerId: formState.layerId.trim() || null,
      });

      await runMultipartUpload(activeSession, formState.file);

      const completedSession = await completeMapSourceUpload(activeSession.id);
      setUploadSession(completedSession);
      setUploadPhase(mapUploadStatusToPhase(completedSession));
      syncUploadProgressFromSession(completedSession);
      setResumeNeedsFile(isMapSourceUploadAwaitingMultipartFile(completedSession.status));
      if (completedSession.status === 'ready') {
        clearPersistedMapSourceUploadSnapshot();
        await mutate();
        closeUploadSheet();
      }
    } catch (err: any) {
      if (err instanceof DOMException && err.name === 'AbortError') {
        setUploadPhase('cancelled');
        setFormError('Upload cancelled.');
      } else {
        setUploadPhase('failed');
        setFormError(err.message ?? 'Upload failed.');
      }
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    const persistedSnapshot = loadPersistedMapSourceUploadSnapshot();
    if (!persistedSnapshot?.sessionId) {
      return;
    }

    let cancelled = false;
    const restoreUploadSession = async () => {
      try {
        const restoredSession = await getMapSourceUpload(persistedSnapshot.sessionId);
        if (cancelled) return;

        if (restoredSession.status === 'ready') {
          clearPersistedMapSourceUploadSnapshot();
          await mutate();
          return;
        }

        if (isMapSourceUploadTerminalStatus(restoredSession.status)) {
          clearPersistedMapSourceUploadSnapshot();
        }

        setUploadSession(restoredSession);
        setUploadPhase(mapUploadStatusToPhase(restoredSession));
        syncUploadProgressFromSession(restoredSession);
        setResumeNeedsFile(isMapSourceUploadAwaitingMultipartFile(restoredSession.status));
        setReplaceTarget(
          persistedSnapshot.replaceTargetId ? ({ id: persistedSnapshot.replaceTargetId } as MapSource) : null,
        );
        setFormState({
          sourceKind: persistedSnapshot.sourceKind ?? 'geojson_file',
          name: persistedSnapshot.name,
          description: persistedSnapshot.description ?? '',
          shareScope: persistedSnapshot.shareScope,
          layerId: persistedSnapshot.layerId ?? '',
          url: '',
          requestRecipeText: '',
          serverFilteringText: '',
          authStrategy: 'none',
          authHeaderName: '',
          authSecret: '',
          file: null,
        });
        setSheetOpen(true);
      } catch {
        clearPersistedMapSourceUploadSnapshot();
      }
    };

    restoreUploadSession();
    return () => {
      cancelled = true;
    };
  }, [mapUploadStatusToPhase, mutate, syncUploadProgressFromSession]);

  useEffect(() => {
    if (!visiblePendingUploads.some(session => isMapSourceUploadBackgroundProcessing(session.status))) {
      return;
    }
    const intervalId = window.setInterval(() => {
      void mutate();
    }, 4000);
    return () => {
      window.clearInterval(intervalId);
    };
  }, [mutate, visiblePendingUploads]);

  useEffect(() => {
    if (!uploadSession?.id) {
      return;
    }
    if (isMapSourceUploadTerminalStatus(uploadSession.status)) {
      return;
    }

    let cancelled = false;
    const intervalId = window.setInterval(async () => {
      try {
        const latestSession = await getMapSourceUpload(uploadSession.id!);
        if (cancelled) {
          return;
        }

        setUploadSession(latestSession);
        setUploadPhase(mapUploadStatusToPhase(latestSession));
        setResumeNeedsFile(isMapSourceUploadAwaitingMultipartFile(latestSession.status));
        if (!isMapSourceUploadAwaitingMultipartFile(latestSession.status)) {
          syncUploadProgressFromSession(latestSession);
        }

        if (latestSession.status === 'ready') {
          clearPersistedMapSourceUploadSnapshot();
          await mutate();
          closeUploadSheet();
        } else if (latestSession.status === 'failed') {
          setSaving(false);
          setFormError(latestSession.error_message ?? 'Upload failed.');
        } else if (latestSession.status === 'cancelled') {
          setSaving(false);
          setFormError('Upload cancelled.');
        } else if (latestSession.status === 'expired') {
          setSaving(false);
          setFormError('Upload session expired. Start a new upload to continue.');
        }
      } catch {
        // Keep the current state visible and retry on the next poll.
      }
    }, 2000);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [closeUploadSheet, mapUploadStatusToPhase, mutate, syncUploadProgressFromSession, uploadSession?.id, uploadSession?.status]);

  const handleAbortUpload = useCallback(async () => {
    uploadAbortControllerRef.current?.abort();
    const sessionId = uploadSession?.id;
    setSaving(false);
    setUploadPhase('cancelled');
    if (!sessionId) {
      clearPersistedMapSourceUploadSnapshot();
      return;
    }
    try {
      const cancelledSession = await abortMapSourceUpload(sessionId);
      setUploadSession(cancelledSession);
      syncUploadProgressFromSession(cancelledSession);
    } catch {
      // The local abort state is still enough to unblock the UI.
    } finally {
      clearPersistedMapSourceUploadSnapshot();
    }
  }, [syncUploadProgressFromSession, uploadSession?.id]);

  const retryGeoJSONValidation = useCallback(async (source: MapSource) => {
    setGeojsonRetryingSourceId(source.id!);
    try {
      const pendingSource = await updateMapSource(source.id!, buildGeoJSONRetryPayload(source)!);
      setDebugTarget(pendingSource);
      if (inspectorTarget?.id === pendingSource.id) {
        await refreshInspectorFromSource(pendingSource);
      }
      await mutate();
      window.setTimeout(async () => {
        try {
          const refreshedSource = await getMapSource(source.id!);
          if (inspectorTarget?.id === refreshedSource.id) {
            await refreshInspectorFromSource(refreshedSource);
          }
          setDebugTarget(refreshedSource);
          await mutate();
        } catch {
          // Keep the pending state visible if the follow-up refresh fails.
        }
      }, 300);
    } catch (err: any) {
      setGeojsonRetryError(err.message);
    } finally {
      setGeojsonRetryingSourceId(null);
    }
  }, [inspectorTarget?.id, mutate, refreshInspectorFromSource]);

  if (!mappingAccess.mappingFeatureEnabled) return <MappingFeatureUnavailable />;
  if (!mappingAccess.mappingPermissionGranted) return <MappingPermissionDenied />;

  const totalLayers = data?.reduce((sum, s) => sum + normalizeVectorLayers(s.vector_layers ?? null).length, 0) ?? 0;
  const total = filteredSources.length;
  const totalPages = Math.max(1, Math.ceil(total / DEFAULT_PAGE_SIZE));
  const pageStart = total > 0 ? (page - 1) * DEFAULT_PAGE_SIZE + 1 : 0;
  const pageEnd = total > 0 ? Math.min(page * DEFAULT_PAGE_SIZE, total) : 0;

  const rangeLabel = useMemo(() => {
    if (!total) return '0 sources found';
    return `${pageStart}-${pageEnd} of ${total} sources`;
  }, [total, pageEnd, pageStart]);

  const uploadProgressPercent = useMemo(() => {
    if (uploadSession && isMapSourceUploadBackgroundProcessing(uploadSession.status)) {
      return estimateMapSourceUploadProgress(uploadSession).percent;
    }
    const totalBytes = uploadSession?.size_bytes ?? formState.file?.size ?? 0;
    if (!totalBytes || totalBytes <= 0) return 0;
    return Math.max(0, Math.min(100, (uploadBytes / totalBytes) * 100));
  }, [formState.file?.size, uploadBytes, uploadSession]);

  const isGeoJSONFileForm = formState.sourceKind === 'geojson_file';
  const isBinaryUploadForm = isGeoJSONFileForm;
  const activeBackgroundProgress =
    uploadSession && isMapSourceUploadBackgroundProcessing(uploadSession.status)
      ? estimateMapSourceUploadProgress(uploadSession)
      : null;
  const uploadBusy =
    isBinaryUploadForm &&
    (saving ||
      ['preparing', 'uploading', 'finalizing', 'queued_processing', 'processing', 'inspecting', 'registering'].includes(
        uploadPhase,
      ));
  const uploadReadyForResume =
    isBinaryUploadForm && Boolean(uploadSession?.id) && isMapSourceUploadAwaitingMultipartFile(uploadSession?.status);
  const uploadCanAbort = isBinaryUploadForm && isMapSourceUploadAwaitingMultipartFile(uploadSession?.status);

  const isInspector = inspectorOpen && inspectorTarget;

  if (isInspector) {
    return (
      <AdminShell
        title={
          <WorksurfaceHeader 
            parentLabel="Map Sources"
            activeLabel={inspectorTarget.name || 'Source'} 
            onBack={handleCloseInspector}
          />
        }
        bodyClassName="flex min-h-0 flex-col"
        flat
      >
        <WorksurfaceTitle 
          title="Map Sources" 
          context={inspectorTarget.name || 'Source'} 
        />
        <WorksurfaceLayout>
          <SidebarPane width={380}>
            <VerticalStack gap={0} className="h-full">
              <PaneSection title="Context">
                <MetricBar items={[
                  { label: 'Layers', value: String(inspectorLayers.length) },
                  { label: 'Status', value: getGeoJSONSourceDiagnostics(inspectorTarget).statusLabel }
                ]} variant="flat" />
                {(() => {
                  const packageSummary = summarizeSalesMapSourcePackage(inspectorTarget);
                  return (
                    <VerticalStack gap={3} className="mt-4 rounded-lg border border-border/70 bg-muted/20 p-4">
                      <HorizontalStack gap={2} className="items-center justify-between">
                        <SectionEyebrow>Sales Map Package</SectionEyebrow>
                        <RegistryStatus label={packageSummary.label} variant={packageSummary.tone} />
                      </HorizontalStack>
                      <DescriptionText>{packageSummary.description}</DescriptionText>
                      <div className="grid gap-2">
                        {Object.values(packageSummary.layers)
                          .filter(layer => layer.present)
                          .map(layer => (
                            <div
                              key={layer.layerId}
                              className="rounded-md border border-border/60 bg-background/80 px-3 py-2 text-xs text-muted-foreground"
                            >
                              <p className="font-medium text-foreground">{formatSalesMapLayerLabel(layer.layerId)}</p>
                              <p className="mt-1">
                                Geometry: expected {layer.geometryExpected}, found {layer.geometryActual ?? 'unknown'}
                              </p>
                              {layer.fieldsVerified ? (
                                <p className="mt-1">
                                  {layer.missingRequiredFields.length === 0
                                    ? 'Required fields verified.'
                                    : `Missing fields: ${layer.missingRequiredFields.join(', ')}`}
                                </p>
                              ) : (
                                <p className="mt-1">Field verification unavailable from current source metadata.</p>
                              )}
                            </div>
                          ))}
                      </div>
                      {packageSummary.warnings.length > 0 ? (
                        <div className="rounded-md border border-amber-500/20 bg-amber-500/10 px-3 py-2 text-xs text-amber-800">
                          {packageSummary.warnings.map((warning, index) => (
                            <p key={`${warning}-${index}`}>{warning}</p>
                          ))}
                        </div>
                      ) : null}
                    </VerticalStack>
                  );
                })()}
                {isGeoJSONSource(inspectorTarget) ? (
                  <VerticalStack gap={3} className="mt-4">
                    <GeoJSONValidationAlert source={inspectorTarget} />
                    <HorizontalStack gap={2}>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => retryGeoJSONValidation(inspectorTarget)}
                        disabled={geojsonRetryingSourceId === inspectorTarget.id}
                      >
                        {geojsonRetryingSourceId === inspectorTarget.id ? 'Retrying…' : 'Retry fetch'}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setGeojsonRetryError(null);
                          setDebugTarget(inspectorTarget);
                          setDebugOpen(true);
                        }}
                      >
                        Debug details
                      </Button>
                    </HorizontalStack>
                  </VerticalStack>
                ) : null}
              </PaneSection>
              <SectionSeparator />
              <Scrollable>
                <PaneSection title="Vector Layers">
                  <MapSourceLayerVisibilityPanel
                    layers={inspectorLayers.map(l => ({ id: l.id, label: l.id, checked: layerVisibility[l.id], color: l.color }))}
                    onToggleLayer={(id: string, checked: boolean) => setLayerVisibility(v => ({ ...v, [id]: checked }))}
                  />
                </PaneSection>
              </Scrollable>
            </VerticalStack>
          </SidebarPane>
          <MainPane>
            <Overlay position="inset" className="p-4">
              <MapPreviewFrame
                status={{ label: inspectorStatus, tone: inspectorStatus === 'success' ? 'success' : 'warning' }}
                viewport={
                  <MapInspectorView
                    styleJson={inspectorStyle}
                    viewState={inspectorViewState}
                    inspectableLayerIds={inspectorLayers.flatMap(layer => layer.styleLayerIds)}
                    onHoverFeatureChange={setHoveredFeature}
                    onPinnedFeatureChange={setPinnedFeature}
                  />
                }
              />
            </Overlay>
            {hoveredFeature && (
              <Overlay position="bottom-left">
                <MapFeatureHoverCard title={hoveredFeature.layerId || 'Feature'} summaryItems={[{ key: 'id', label: 'ID', value: String(hoveredFeature.id) }]} />
              </Overlay>
            )}
          </MainPane>
          {pinnedFeature && (
            <SidebarPane width={400} position="right" className="bg-background">
              <PaneHeader title="Details" actions={<Button variant="ghost" size="icon" onClick={() => setPinnedFeature(null)}><MoreHorizontal /></Button>} />
              <Scrollable>
                <PaneSection title="Properties">
                  <PropertyGrid items={Object.entries(pinnedFeature.properties || {}).map(([k, v]) => ({ label: k, value: String(v), isTechnical: true }))} />
                </PaneSection>
              </Scrollable>
            </SidebarPane>
          )}
        </WorksurfaceLayout>
        <GeoJSONDebugDialog source={debugTarget} open={debugOpen} canRetry={true} retrying={!!geojsonRetryingSourceId} onOpenChange={setDebugOpen} onRetry={() => retryGeoJSONValidation(debugTarget!)} />
      </AdminShell>
    );
  }

  return (
    <>
      <WorksurfaceTitle title="Map Sources" context="Library" />
      <ManagementRegistry
        parentLabel="Mapping"
        activeLabel="Map Sources"
        description="Build the source library with managed GeoJSON datasets and derived runtime tiles, inspect their geometry makeup, and verify readiness before using them in portal map authoring flows."
        headerActions={
          <Button onClick={() => openUpload()}>
            <Plus className="mr-2 h-4 w-4" />
            New Source
          </Button>
        }
        metrics={[
          { label: 'Total Sources', value: String(data?.length ?? 0) }, 
          { label: 'Vector Layers', value: String(totalLayers) },
          { label: 'Pending Uploads', value: String(visiblePendingUploads.filter(session => isMapSourceUploadBackgroundProcessing(session.status)).length) },
          { label: 'Page', value: `${page} of ${totalPages}` }
        ]}
        toolbar={
          <CondensedToolbar>
            <ToolbarItem label="Search" flex>
              <SilkInput 
                placeholder="Filter by name, description, or ID..." 
                value={search} 
                onChange={e => setSearch(e.target.value)} 
                icon={<Search className="h-3.5 w-3.5" />} 
              />
            </ToolbarItem>
            <ToolbarItem label="Scope">
              <SilkSelect 
                value={shareScopeFilter} 
                onValueChange={v => setShareScopeFilter(v as any)} 
                options={[
                  { label: 'All Scopes', value: 'all' }, 
                  { label: 'Tenant', value: 'tenant' }, 
                  { label: 'Public', value: 'public' }, 
                  { label: 'Shared', value: 'shared' }
                ]} 
                className="w-40"
              />
            </ToolbarItem>
          </CondensedToolbar>
        }
        isLoading={isLoading}
        loadingLabel="Loading map sources..."
        rangeLabel={rangeLabel}
        pagination={
          <Pagination 
            page={page}
            totalPages={totalPages}
            onPageChange={setPage}
            label={rangeLabel}
          />
        }
        bodyClassName="pb-10"
      >
        {routingGraphStatus.visible ? (
          <RoutingGraphImportStatusView
            loading={routingGraphStatus.loading}
            refreshing={routingGraphStatus.refreshing}
            stale={routingGraphStatus.stale}
            error={routingGraphStatus.error}
            runs={routingGraphStatus.runs}
            servingGraph={routingGraphStatus.servingGraph}
            onRefresh={() => { void routingGraphStatus.refresh(); }}
          />
        ) : null}
        {visiblePendingUploads.length > 0 ? (
          <Callout variant="info">
            <div className="space-y-3">
              <p className="font-medium text-foreground">
                Upload activity stays here until the source is fully registered.
              </p>
              <div className="space-y-2">
                {visiblePendingUploads.slice(0, 6).map(session => {
                  const progress = estimateMapSourceUploadProgress(session);
                  return (
                  <div
                    key={session.id}
                    className="rounded-md border border-border/60 bg-background/80 px-3 py-2 text-sm"
                  >
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div>
                        <p className="font-medium text-foreground">{session.name ?? session.filename ?? 'Upload session'}</p>
                        <p className="text-xs text-muted-foreground">
                          {session.layer_id ? `${session.layer_id} • ` : ''}
                          {session.filename ?? 'Unknown file'}
                        </p>
                      </div>
                      <RegistryStatus
                        label={uploadPhaseLabelForSession(mapUploadStatusToPhase(session))}
                        variant={
                          session.status === 'failed' || session.status === 'expired'
                            ? 'error'
                            : session.status === 'cancelled'
                              ? 'muted'
                              : 'warning'
                        }
                      />
                    </div>
                    {isMapSourceUploadBackgroundProcessing(session.status) ? (
                      <div className="mt-2 space-y-1.5">
                        <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
                          <span>{progress.phaseLabel}</span>
                          <span>{progress.percent === null ? 'Working…' : `${progress.percent}%`}</span>
                        </div>
                        <div className="h-1.5 overflow-hidden rounded-full bg-muted">
                          <div
                            className={`h-full bg-primary transition-all ${progress.indeterminate ? 'animate-pulse' : ''}`}
                            style={{
                              width: `${progress.percent === null ? 35 : Math.max(4, Math.min(100, progress.percent))}%`,
                            }}
                          />
                        </div>
                      </div>
                    ) : null}
                    {session.error_message ? (
                      <p className="mt-2 text-xs text-amber-800">{session.error_message}</p>
                    ) : null}
                  </div>
                  );
                })}
              </div>
            </div>
          </Callout>
        ) : null}
        <RegistryList>
          {filteredSources.length === 0 ? (
            <VerticalStack gap={1} className="p-12 items-center">
              <SectionEyebrow className="text-sm">No sources found</SectionEyebrow>
              <DescriptionText>Try adjusting your search or filters.</DescriptionText>
            </VerticalStack>
          ) : (
            filteredSources.map(s => {
              const layers = normalizeVectorLayers(s.vector_layers ?? null);
              const isGeoJSON = isGeoJSONSource(s);
              const diagnostics = isGeoJSON ? getGeoJSONSourceDiagnostics(s) : null;
              const packageSummary = summarizeSalesMapSourcePackage(s);
              const readOnly = isPortalAssetReadOnly(s.tenant_id, mappingAccess.featureTenantId);

              return (
                <RegistryRow
                  key={s.id}
                  onClick={() => openInspector(s)}
                  avatar={<SourceIcon kind={isGeoJSON ? 'geojson' : 'pmtiles'} />}
                  title={s.name || 'Untitled Source'}
                  subtitle={s.description}
                  metadata={
                    <HorizontalStack gap={2}>
                      <TechnicalValue label="ID" value={s.id || '—'} />
                      <RegistryStatus label={isGeoJSON ? 'GeoJSON' : 'Managed GeoJSON'} variant="muted" />
                      <RegistryStatus label={formatShareScopeLabel(s.share_scope)} variant="muted" />
                      {packageSummary.tier !== 'none' ? (
                        <RegistryStatus label={packageSummary.label} variant={packageSummary.tone} />
                      ) : null}
                      {isGeoJSON && diagnostics && (
                        <RegistryStatus 
                          label={diagnostics.statusLabel} 
                          variant={
                            diagnostics.state === 'error' ? 'error' : 
                            diagnostics.state === 'pending' ? 'warning' : 'success'
                          } 
                        />
                      )}
                      <RegistryStatus label={`${layers.length} ${layers.length === 1 ? 'Layer' : 'Layers'}`} variant="muted" />
                    </HorizontalStack>
                  }
                  actions={
                    <HorizontalStack gap={2}>
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={(e) => { e.stopPropagation(); openInspector(s); }}>
                        <span className="sr-only">Inspect</span>
                        <Info className="h-4 w-4" />
                      </Button>
                      {!readOnly && (
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={(e) => { e.stopPropagation(); openUpload(s); }}>
                          <span className="sr-only">Edit</span>
                          <Settings2 className="h-4 w-4" />
                        </Button>
                      )}
                    </HorizontalStack>
                  }
                />
              );
            })
          )}
        </RegistryList>
      </ManagementRegistry>

      <Sheet
        open={sheetOpen}
        onOpenChange={open => {
          if (!open && uploadBusy) {
            return;
          }
          setSheetOpen(open);
          if (!open) {
            closeUploadSheet();
          }
        }}
      >
        <SheetContent side="right" size="lg">
          <SheetHeader>
            <SheetTitle>
              {replaceTarget
                ? isGeoJSONSource(replaceTarget)
                  ? 'Edit Live GeoJSON'
                  : 'Replace Managed GeoJSON'
                : formState.sourceKind === 'geojson_http'
                  ? 'Create Live GeoJSON'
                  : 'Upload GeoJSON'}
            </SheetTitle>
            <SheetDescription>
              {replaceTarget
                ? isGeoJSONSource(replaceTarget)
                  ? 'Update the upstream URL or metadata for this live GeoJSON source.'
                  : 'Upload a GeoJSON file, verify it against the selected source contract, and replace the current managed runtime source.'
                : formState.sourceKind === 'geojson_http'
                  ? 'Create a live GeoJSON source that validates in the background.'
                  : 'Upload a GeoJSON file and let the platform build managed runtime artifacts for portal map authoring.'}
            </SheetDescription>
          </SheetHeader>
          <VerticalStack gap={4} className="mt-6">
            <VerticalStack gap={2}>
              <Label>Source Type</Label>
              <Select
                value={formState.sourceKind}
                onValueChange={value =>
                  setFormState(prev => ({
                    ...prev,
                    sourceKind: value as MapSourceFormKind,
                    authStrategy: value === 'geojson_http' ? prev.authStrategy : 'none',
                    authHeaderName: value === 'geojson_http' ? prev.authHeaderName : '',
                    authSecret: value === 'geojson_http' ? prev.authSecret : '',
                    requestRecipeText: value === 'geojson_http' ? prev.requestRecipeText : '',
                    serverFilteringText: value === 'geojson_http' ? prev.serverFilteringText : '',
                    file: value === 'geojson_file' ? prev.file : null,
                  }))
                }
                disabled={Boolean(uploadSession?.id) || isGeoJSONSource(replaceTarget)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Source type" />
                </SelectTrigger>
                <SelectContent>
                  {!isGeoJSONSource(replaceTarget) ? <SelectItem value="geojson_file">GeoJSON Upload</SelectItem> : null}
                  {!replaceTarget ? <SelectItem value="geojson_http">Live GeoJSON</SelectItem> : null}
                </SelectContent>
              </Select>
            </VerticalStack>
            <VerticalStack gap={2}>
              <Label>Name</Label>
              <Input id="source-name" value={formState.name} onChange={e => setFormState(v => ({ ...v, name: e.target.value }))} />
            </VerticalStack>
            <VerticalStack gap={2}>
              <Label>Description</Label>
              <Input id="source-description" value={formState.description} onChange={e => setFormState(v => ({ ...v, description: e.target.value }))} />
            </VerticalStack>
            <VerticalStack gap={2}>
              <Label>Share Scope</Label>
              <Select
                value={formState.shareScope}
                onValueChange={value => setFormState(prev => ({ ...prev, shareScope: value as ShareScope }))}
                disabled={Boolean(replaceTarget)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Scope" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="shared">Shared</SelectItem>
                  <SelectItem value="tenant">Tenant</SelectItem>
                </SelectContent>
              </Select>
            </VerticalStack>
            {formState.sourceKind === 'geojson_http' ? (
              <>
                <VerticalStack gap={2}>
                  <Label>GeoJSON URL</Label>
                  <Input id="source-url" value={formState.url} onChange={e => setFormState(v => ({ ...v, url: e.target.value }))} />
                </VerticalStack>
                <VerticalStack gap={2}>
                  <Label>Request Recipe JSON</Label>
                  <Textarea
                    rows={8}
                    value={formState.requestRecipeText}
                    onChange={e => setFormState(v => ({ ...v, requestRecipeText: e.target.value }))}
                  />
                </VerticalStack>
                <VerticalStack gap={2}>
                  <Label>Server Filtering JSON</Label>
                  <Textarea
                    rows={8}
                    value={formState.serverFilteringText}
                    onChange={e => setFormState(v => ({ ...v, serverFilteringText: e.target.value }))}
                  />
                </VerticalStack>
                <VerticalStack gap={2}>
                  <Label>Upstream Auth</Label>
                  <Select
                    value={formState.authStrategy}
                    onValueChange={value => setFormState(prev => ({ ...prev, authStrategy: value as GeoJSONFormAuthStrategy }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Auth strategy" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      <SelectItem value="bearer_token">Bearer Token</SelectItem>
                      <SelectItem value="header_api_key">Header API Key</SelectItem>
                    </SelectContent>
                  </Select>
                </VerticalStack>
                {formState.authStrategy === 'header_api_key' ? (
                  <VerticalStack gap={2}>
                    <Label>Header Name</Label>
                    <Input value={formState.authHeaderName} onChange={e => setFormState(v => ({ ...v, authHeaderName: e.target.value }))} />
                  </VerticalStack>
                ) : null}
                {formState.authStrategy !== 'none' ? (
                  <VerticalStack gap={2}>
                    <Label>{formState.authStrategy === 'bearer_token' ? 'Bearer Token' : 'Header Value'}</Label>
                    <Input
                      type="password"
                      value={formState.authSecret}
                      onChange={e => setFormState(v => ({ ...v, authSecret: e.target.value }))}
                    />
                  </VerticalStack>
                ) : null}
              </>
            ) : (
              <>
                <div className="rounded-lg border border-border/70 bg-muted/20 p-4">
                  <p className="text-sm font-medium text-foreground">Sales Map Source Package v1</p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Publish <span className="font-medium text-foreground">sales_locations</span> as the required canonical
                    layer. Standardized optional layers are <span className="font-medium text-foreground">sales_markets</span>,
                    <span className="font-medium text-foreground"> network_fiber</span>, and
                    <span className="font-medium text-foreground"> fiber_access_points</span>.
                  </p>
                  <p className="mt-2 text-xs text-muted-foreground">
                    Keep tenant CRM or billing identifiers out of the required browser-facing contract. Do not rely on
                    ACD-specific fields such as <span className="font-medium text-foreground">customer_pkid</span>,
                    <span className="font-medium text-foreground"> customer_id</span>, or manual Google-address state.
                  </p>
                </div>
                {isGeoJSONFileForm ? (
                  <VerticalStack gap={2}>
                    <Label>Layer ID</Label>
                    <Input
                      id="source-layer-id"
                      value={formState.layerId}
                      placeholder="sales_locations"
                      onChange={e => setFormState(v => ({ ...v, layerId: e.target.value }))}
                    />
                    <DescriptionText className="text-xs">
                      Upload one canonical layer per GeoJSON file. Use values such as <span className="font-medium">sales_locations</span>,
                      <span className="font-medium"> sales_markets</span>, <span className="font-medium"> network_fiber</span>, or
                      <span className="font-medium"> fiber_access_points</span>. Leave blank to derive the layer id from the filename.
                    </DescriptionText>
                  </VerticalStack>
                ) : null}
                <VerticalStack gap={2}>
                  <Label>GeoJSON File</Label>
                  <Input
                    id="source-file"
                    type="file"
                    accept=".geojson,.json,application/geo+json,application/json"
                    onChange={e => {
                      const selected = e.target.files?.[0] ?? null;
                      setFormState(v => {
                        const next = { ...v, file: selected };
                        if (selected && !v.layerId && isGeoJSONFileForm) {
                          next.layerId = selected.name.replace(/\.[^.]+$/, '');
                        }
                        return next;
                      });
                    }}
                  />
                  {replaceTarget?.id ? <DescriptionText className="text-xs">Replacing source {replaceTarget.id}</DescriptionText> : null}
                  {resumeNeedsFile && uploadSession?.filename ? (
                    <DescriptionText className="text-xs text-amber-700">
                      Re-select <span className="font-medium">{uploadSession.filename}</span> to resume this upload session.
                    </DescriptionText>
                  ) : null}
                </VerticalStack>
              </>
            )}
            {isBinaryUploadForm && uploadSession?.id ? (
              <VerticalStack gap={3} className="rounded-lg border border-border/70 bg-muted/40 p-4">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-medium">{uploadPhaseLabel}</p>
                    <p className="text-xs text-muted-foreground">Session {uploadSession.id}</p>
                  </div>
                  <div className="text-right text-xs text-muted-foreground">
                    <div>
                      {activeBackgroundProgress
                        ? `${activeBackgroundProgress.percent}%`
                        : `${formatBytes(uploadBytes) ?? '0 B'} / ${formatBytes(uploadSession.size_bytes) ?? '—'}`}
                    </div>
                    {uploadRateBytes > 0 ? <div>{formatBytes(uploadRateBytes)}/s</div> : null}
                    {uploadEtaSeconds != null ? <div>ETA {formatUploadDuration(uploadEtaSeconds)}</div> : null}
                    {activeBackgroundProgress?.elapsedLabel ? <div>Elapsed {activeBackgroundProgress.elapsedLabel}</div> : null}
                  </div>
                </div>
                <div className="h-2 overflow-hidden rounded-full bg-muted">
                  <div className="h-full bg-primary transition-all" style={{ width: `${uploadProgressPercent}%` }} />
                </div>
                <div className="grid gap-2 text-xs text-muted-foreground sm:grid-cols-2">
                  <div>
                    Uploaded parts: {(uploadSession.uploaded_parts?.length ?? 0)} / {uploadSession.total_parts ?? '—'}
                  </div>
                  <div>Expires: {formatDate(uploadSession.expires_at)}</div>
                </div>
              </VerticalStack>
            ) : null}
            {isBinaryUploadForm && uploadBusy && uploadPhase !== 'uploading' ? (
              <div className="rounded-md border border-border/70 bg-background/70 px-3 py-2 text-xs text-muted-foreground">
                The browser has finished sending bytes. The backend is now finalizing the upload, verifying the GeoJSON, building runtime tiles, inspecting the generated archive, and registering the source.
              </div>
            ) : null}
            {isBinaryUploadForm && uploadPhase === 'ready' && uploadSession?.map_source?.id ? (
              <div className="rounded-md border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-xs text-emerald-700">
                Source {uploadSession.map_source.id} is ready.
              </div>
            ) : null}
            {isBinaryUploadForm && uploadPhase === 'cancelled' ? (
              <div className="rounded-md border border-border/70 bg-background/70 px-3 py-2 text-xs text-muted-foreground">
                Upload cancelled. You can close this sheet or start a new upload.
              </div>
            ) : null}
            {isBinaryUploadForm && uploadPhase === 'expired' ? (
              <div className="rounded-md border border-destructive/20 bg-destructive/5 px-3 py-2 text-xs text-destructive">
                Upload session expired before completion. Start a new upload session to continue.
              </div>
            ) : null}
            {isBinaryUploadForm && uploadPhase === 'failed' && uploadSession?.error_message ? (
              <div className="rounded-md border border-destructive/20 bg-destructive/5 px-3 py-2 text-xs text-destructive">
                {uploadSession.error_message}
              </div>
            ) : null}
            {formError ? <Callout variant="error">{formError}</Callout> : null}
            <HorizontalStack gap={2} className="justify-end">
              {uploadCanAbort ? (
                <Button variant="outline" onClick={handleAbortUpload}>Cancel Upload</Button>
              ) : (
                <Button variant="outline" onClick={closeUploadSheet} disabled={uploadBusy}>Cancel</Button>
              )}
              <Button onClick={handleSubmit} disabled={uploadBusy || (uploadReadyForResume && resumeNeedsFile && !formState.file)}>
                {uploadReadyForResume
                  ? 'Resume Upload'
                  : uploadBusy
                    ? `${uploadPhaseLabel}...`
                    : replaceTarget
                      ? isGeoJSONSource(replaceTarget)
                        ? 'Save'
                        : formState.sourceKind === 'geojson_file'
                          ? 'Convert and Replace'
                          : 'Upload Replacement'
                      : formState.sourceKind === 'geojson_http'
                        ? 'Create'
                        : formState.sourceKind === 'geojson_file'
                          ? 'Convert and Create'
                          : 'Start Upload'}
              </Button>
            </HorizontalStack>
          </VerticalStack>
        </SheetContent>
      </Sheet>

      <MappingAssetAccessSheet 
        open={accessOpen} 
        onOpenChange={setAccessOpen} 
        asset={accessTarget} 
        assetTypeLabel="Map source"
        featureTenantId={mappingAccess.featureTenantId} 
        loadShares={listMapSourceShares} 
        saveAccess={async (a, i) => { await updateMapSource(a.id!, { share_scope: i.shareScope }); await replaceMapSourceShares(a.id!, i.tenantIds); await mutate(); }} 
      />
    </>
  );
}
